import { decide } from '@emr/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createKioskToken, revokeKioskToken } from '../../api/kiosk.ts';
import { errorMessage } from '../../api/client.ts';
import { useGrants } from '../../auth/sessionContext.ts';
import { Button, ErrorText, Panel } from '../../components/primitives.tsx';

/**
 * Staff-facing entry point for kiosk check-in (`BACKLOG.md` C7): start a token-scoped session for
 * this patient, hand the resulting link to a tablet, revoke it early if needed. Embedded into
 * `PatientChartPage.tsx` (one import, one JSX line) rather than a standalone route — the natural
 * place a front-desk user already is when they decide to start a check-in.
 *
 * Gated on `patient:read` — matching `kiosk/routes.ts`'s own server-side capability choice and its
 * stated reasoning (issuing a token does not itself write to the patient record). The page this panel
 * lives on is already gated on the same capability at the route level (`GatedRoute` in
 * `PatientChartPage.tsx`), so this check is defense-in-depth against nothing reachable today — kept
 * anyway so this component stays correct if it is ever reused somewhere with a looser gate.
 */
export function KioskTokenPanel({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('kiosk');
  const grants = useGrants();
  const [active, setActive] = useState<{ id: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => createKioskToken(patientId),
    onSuccess: (response) => {
      const { id, token } = response.token;
      // `token` is only ever present on the create response (`@emr/shared`'s `kioskTokenResponseSchema`
      // comment) — always true here since this is that exact response, never narrowed by a stale cache.
      if (token !== undefined) setActive({ id, token });
      setCopied(false);
    },
  });
  const revoke = useMutation({
    mutationFn: (tokenId: string) => revokeKioskToken(tokenId),
    onSuccess: () => {
      setActive(null);
    },
  });

  if (!decide(grants, 'patient:read')) return null;

  const link = active !== null ? `${window.location.origin}/kiosk#token=${active.token}` : null;

  return (
    <Panel title={t('staffPanelTitle')} className="mb-4">
      {active === null ? (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-ink-muted">{t('staffPanelBody')}</p>
          <Button
            onClick={() => {
              create.mutate();
            }}
            pending={create.isPending}
            className="self-start"
          >
            {t('staffCreateButton')}
          </Button>
          {create.isError ? <ErrorText message={errorMessage(create.error)} /> : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-ink">{t('staffLinkReady')}</p>
          <code className="block rounded-control border border-line bg-field p-2 text-xs break-all text-ink">
            {link}
          </code>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                if (link === null) return;
                navigator.clipboard
                  .writeText(link)
                  .then(() => {
                    setCopied(true);
                  })
                  .catch(() => {
                    // Clipboard access can be refused by browser policy — the link is already
                    // visible and selectable in the <code> block above, so this is a convenience,
                    // not the only way to get it.
                  });
              }}
            >
              {copied ? t('staffCopiedButton') : t('staffCopyButton')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                revoke.mutate(active.id);
              }}
              pending={revoke.isPending}
            >
              {t('staffRevokeButton')}
            </Button>
          </div>
          {revoke.isError ? <ErrorText message={errorMessage(revoke.error)} /> : null}
        </div>
      )}
    </Panel>
  );
}
